import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'motion/react';
import { MessageSquare, Search, Circle } from 'lucide-react';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { useTeamChat } from '../store/TeamChatContext';
import { decodeChatPlainText } from '../utils/sanitize';
import { cn } from '../../../components/ui/utils';

type SortKey = 'recent' | 'online' | 'unread';

export function TeamChatHeaderTrigger() {
  const navigate = useNavigate();
  const location = useLocation();
  const { teammates, unreadTotal, openFloatingChat, refreshDirectory } = useTeamChat();
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<SortKey>('recent');
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!menuOpen) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [menuOpen]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const filtered = useMemo(() => {
    let rows = teammates.filter((t) => {
      if (!q.trim()) return true;
      const s = q.toLowerCase();
      const m = t.member;
      return (
        m.firstName.toLowerCase().includes(s) ||
        m.lastName.toLowerCase().includes(s) ||
        m.email.toLowerCase().includes(s) ||
        m.roleName.toLowerCase().includes(s)
      );
    });
    if (sort === 'online') {
      rows = [...rows].sort((a, b) => {
        const rank = (x: string) => (x === 'available' ? 0 : x === 'away' ? 1 : 2);
        return rank(a.availability) - rank(b.availability);
      });
    } else if (sort === 'unread') {
      rows = [...rows].sort((a, b) => b.unread - a.unread);
    } else {
      rows = [...rows].sort((a, b) => (b.lastAt || 0) - (a.lastAt || 0));
    }
    return rows;
  }, [q, sort, teammates]);

  const closeAnd = (fn: () => void) => {
    setMenuOpen(false);
    fn();
  };

  return (
    <DropdownMenu
      modal
      open={menuOpen}
      onOpenChange={(open: boolean) => {
        setMenuOpen(open);
        if (open) void refreshDirectory();
      }}
    >
      <DropdownMenuTrigger asChild>
        <motion.button
          type="button"
          aria-label="Open team chat"
          aria-expanded={menuOpen}
          className="relative p-2 md:p-3 rounded-lg bg-card-bg/50 hover:bg-card-bg/80 border border-card-border/30 hover:border-card-border/60 transition-all duration-300 ease-[cubic-bezier(0.25,0.46,0.45,0.94)] group backdrop-blur-sm"
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.97 }}
        >
          <MessageSquare className="w-4 h-4 text-slate-400 group-hover:text-cyber-cyan transition-colors" />
          {unreadTotal > 0 ? (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 min-w-[1.1rem] h-4 md:h-5 px-1 flex items-center justify-center text-[10px] border-0"
            >
              {unreadTotal > 99 ? '99+' : unreadTotal}
            </Badge>
          ) : null}
        </motion.button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className={cn(
          'w-[min(100vw-2rem,22rem)] max-h-[min(72vh,28rem)] overflow-hidden flex flex-col p-0',
          'border border-card-border/50 bg-card-bg/95 text-foreground backdrop-blur-xl shadow-2xl shadow-black/40',
          'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2',
        )}
        onCloseAutoFocus={(e) => (e as Event).preventDefault()}
      >
        <div className="p-3 border-b border-border/60 space-y-2">
          <DropdownMenuLabel className="p-0 text-sm font-semibold text-foreground">Team chat</DropdownMenuLabel>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search teammates…"
              className="pl-8 h-9 bg-background/70 text-sm"
            />
          </div>
          <Select value={sort} onValueChange={(v: string) => setSort(v as SortKey)}>
            <SelectTrigger className="h-9 bg-background/70 text-xs">
              <SelectValue placeholder="Sort" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recent">Recent activity</SelectItem>
              <SelectItem value="online">Online first</SelectItem>
              <SelectItem value="unread">Unread first</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="overflow-y-auto flex-1 py-1">
          {filtered.map((row) => {
            const m = row.member;
            const label = row.isSelf ? `${m.firstName} (you)` : `${m.firstName} ${m.lastName}`.trim();
            const online = row.availability === 'available';
            return (
              <div
                key={m.id}
                className="flex gap-2 px-3 py-2.5 rounded-lg mx-1 mb-0.5 hover:bg-muted/50 cursor-default border border-transparent hover:border-border/40 transition-colors duration-200 ease-[cubic-bezier(0.25,0.46,0.45,0.94)]"
              >
                <div className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-cyber-cyan/25 to-cyber-blue/35 text-xs font-semibold text-white">
                  {(m.firstName?.[0] || '?') + (m.lastName?.[0] || '')}
                  <Circle
                    className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 ${
                      online ? 'text-emerald-400 fill-emerald-400' : 'text-slate-500 fill-slate-600'
                    }`}
                    aria-hidden
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className="truncate text-sm font-medium text-foreground">{label}</p>
                    {row.unread > 0 ? (
                      <Badge variant="secondary" className="text-[10px] shrink-0">
                        {row.unread}
                      </Badge>
                    ) : null}
                  </div>
                  <p className="truncate text-[11px] text-muted-foreground">{m.roleName}</p>
                  <p className="mt-0.5 truncate text-[11px] text-muted-foreground/90">
                    {row.lastPreview ? decodeChatPlainText(row.lastPreview).slice(0, 80) : 'No messages yet'}
                  </p>
                  <p className="mt-0.5 text-[10px] text-muted-foreground">{row.lastActiveLabel}</p>
                  <div className="mt-2 flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      className="h-7 text-xs"
                      disabled={row.isSelf}
                      onClick={() => closeAnd(() => openFloatingChat(m.id))}
                    >
                      Open chat
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs text-cyber-cyan"
                      onClick={() =>
                        closeAnd(() => navigate(`/team/chat?with=${encodeURIComponent(m.id)}`))
                      }
                    >
                      In module
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <DropdownMenuSeparator className="m-0" />
        <Button
          type="button"
          variant="ghost"
          className="w-full rounded-none justify-center text-cyber-cyan h-10"
          onClick={() => closeAnd(() => navigate('/team/chat'))}
        >
          Open Team Chat center
        </Button>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
